import { useEffect, useState } from 'react'
import nexusApi from '../../services/nexusApi'
import {
  Plus, Send, ChevronRight, CheckCircle, Clock, AlertTriangle,
  X, Zap, Bug, Shield, Wrench, Eye, BarChart2, RefreshCw
} from 'lucide-react'

const NX = {
  bg: '#0D1117', surface: '#161B22', surface2: '#1C2330', border: '#2A3441',
  text: '#E6EDF3', textDim: '#8B949E', accent: '#0066FF',
  green: '#3DB54A', amber: '#F5921E', red: '#EF4444',
}

const TYPE_META: Record<string, { icon: any; color: string; label: string }> = {
  feature:     { icon: Zap,           color: '#0066FF', label: 'Feature' },
  fix:         { icon: Bug,           color: '#F5921E', label: 'Bug Fix' },
  security:    { icon: Shield,        color: '#EF4444', label: 'Security' },
  maintenance: { icon: Wrench,        color: '#8B949E', label: 'Maintenance' },
}

const SEVERITY_BADGE: Record<string, { bg: string; color: string }> = {
  normal:    { bg: '#8B949E22', color: '#8B949E' },
  important: { bg: '#F5921E22', color: '#F5921E' },
  critical:  { bg: '#EF444422', color: '#EF4444' },
}

function StatusBar({ acknowledged, notified, pending, total }: any) {
  if (total === 0) return <span style={{ color: NX.textDim, fontSize: '0.75rem' }}>No tenants queued</span>
  const ackPct = Math.round((acknowledged / total) * 100)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: NX.border, borderRadius: 3, overflow: 'hidden', minWidth: 100 }}>
        <div style={{ height: '100%', width: `${ackPct}%`, background: NX.green, borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
      <span style={{ color: NX.textDim, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
        {acknowledged}/{total} ack'd
      </span>
    </div>
  )
}

export default function NexusUpdates() {
  const [updates, setUpdates] = useState<any[]>([])
  const [selected, setSelected] = useState<any>(null)
  const [rollout, setRollout] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [saving, setSaving] = useState(false)

  const [form, setForm] = useState({
    version: '', title: '', body: '', type: 'feature',
    severity: 'normal', requires_ack: true, target_plans: [] as string[],
  })

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    try {
      const r = await nexusApi.get('/updates')
      setUpdates(r.data.data || [])
    } catch {}
    setLoading(false)
  }

  async function selectUpdate(u: any) {
    setSelected(u)
    try {
      const r = await nexusApi.get(`/updates/${u.id}/rollout`)
      setRollout(r.data.data || [])
    } catch {}
  }

  async function handleCreate() {
    if (!form.version || !form.title || !form.body) return
    setSaving(true)
    try {
      await nexusApi.post('/updates', form)
      setShowCreate(false)
      setForm({ version: '', title: '', body: '', type: 'feature', severity: 'normal', requires_ack: true, target_plans: [] })
      await load()
    } catch (e: any) { alert(e.response?.data?.error || 'Failed') }
    setSaving(false)
  }

  async function publish(id: string) {
    if (!confirm('Publish this update? It will be queued for all eligible tenants.')) return
    try {
      const r = await nexusApi.post(`/updates/${id}/publish`)
      alert(`Published — queued for ${r.data.queued_tenants} tenants`)
      await load()
      if (selected?.id === id) selectUpdate({ id })
    } catch (e: any) { alert(e.response?.data?.error || 'Failed') }
  }

  async function notify(id: string) {
    if (!confirm('Send in-app notifications to all pending tenants?')) return
    try {
      const r = await nexusApi.post(`/updates/${id}/notify`)
      alert(`Notified ${r.data.notified} tenants`)
      await load()
      if (selected?.id === id) selectUpdate({ id })
    } catch (e: any) { alert(e.response?.data?.error || 'Failed') }
  }

  const PLAN_OPTS = ['trial', 'standard', 'professional', 'enterprise']

  if (loading) return <div style={{ padding: '2rem', color: NX.textDim }}>Loading updates…</div>

  return (
    <div style={{ padding: '2rem', maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.75rem' }}>
        <div>
          <h1 style={{ color: NX.text, fontSize: '1.375rem', fontWeight: 700, margin: 0 }}>Platform Updates</h1>
          <p style={{ color: NX.textDim, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            Create releases, push notifications, and track per-tenant acknowledgement
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} style={{
          display: 'flex', alignItems: 'center', gap: 6, background: NX.accent,
          border: 'none', borderRadius: 8, color: '#fff', padding: '0.625rem 1.125rem',
          cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600,
        }}>
          <Plus size={15} /> New Update
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: '1.5rem' }}>
        {/* Update list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {updates.length === 0 && (
            <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, padding: '3rem', textAlign: 'center', color: NX.textDim }}>
              No updates yet. Create your first platform update.
            </div>
          )}
          {updates.map(u => {
            const meta = TYPE_META[u.type] || TYPE_META.feature
            const sev = SEVERITY_BADGE[u.severity] || SEVERITY_BADGE.normal
            const isActive = selected?.id === u.id
            return (
              <div key={u.id}
                onClick={() => selectUpdate(u)}
                style={{
                  background: isActive ? NX.surface2 : NX.surface,
                  border: `1px solid ${isActive ? NX.accent : NX.border}`,
                  borderRadius: 12, padding: '1rem 1.25rem', cursor: 'pointer',
                  transition: 'border-color 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: `${meta.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <meta.icon size={15} color={meta.color} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ color: NX.text, fontWeight: 600, fontSize: '0.9375rem' }}>{u.title}</span>
                        <span style={{ color: NX.textDim, fontSize: '0.75rem' }}>v{u.version}</span>
                        <span style={{ background: sev.bg, color: sev.color, fontSize: '0.6875rem', fontWeight: 700, padding: '1px 7px', borderRadius: 99, textTransform: 'uppercase' }}>
                          {u.severity}
                        </span>
                        <span style={{ background: `${meta.color}18`, color: meta.color, fontSize: '0.6875rem', padding: '1px 7px', borderRadius: 99 }}>
                          {meta.label}
                        </span>
                        {!u.published_at && (
                          <span style={{ background: '#8B949E22', color: '#8B949E', fontSize: '0.6875rem', padding: '1px 7px', borderRadius: 99 }}>Draft</span>
                        )}
                      </div>
                      <div style={{ color: NX.textDim, fontSize: '0.75rem', marginTop: 4 }}>
                        {u.published_at
                          ? `Published ${new Date(u.published_at).toLocaleDateString('en-AU')}`
                          : `Created ${new Date(u.created_at).toLocaleDateString('en-AU')}`
                        } · by {u.created_by}
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={15} color={NX.textDim} style={{ flexShrink: 0, marginTop: 4 }} />
                </div>

                {u.published_at && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <StatusBar
                      acknowledged={u.acknowledged} notified={u.notified}
                      pending={u.pending} total={u.total_tenants}
                    />
                  </div>
                )}

                {u.published_at && (
                  <div style={{ display: 'flex', gap: 6, marginTop: '0.75rem' }}>
                    {u.pending > 0 && (
                      <button onClick={e => { e.stopPropagation(); notify(u.id) }} style={{
                        display: 'flex', alignItems: 'center', gap: 4, background: `${NX.amber}18`,
                        border: `1px solid ${NX.amber}44`, borderRadius: 6, color: NX.amber,
                        padding: '3px 10px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600,
                      }}>
                        <Send size={11} /> Notify {u.pending} pending
                      </button>
                    )}
                    {u.acknowledged === u.total_tenants && u.total_tenants > 0 && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: NX.green, fontSize: '0.75rem', fontWeight: 600 }}>
                        <CheckCircle size={12} /> Fully rolled out
                      </span>
                    )}
                  </div>
                )}

                {!u.published_at && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <button onClick={e => { e.stopPropagation(); publish(u.id) }} style={{
                      display: 'flex', alignItems: 'center', gap: 4, background: `${NX.accent}18`,
                      border: `1px solid ${NX.accent}44`, borderRadius: 6, color: NX.accent,
                      padding: '3px 10px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600,
                    }}>
                      <Send size={11} /> Publish &amp; Queue
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Rollout detail panel */}
        {selected && (
          <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h2 style={{ color: NX.text, fontSize: '0.9375rem', fontWeight: 600, margin: 0 }}>Rollout Status</h2>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => selectUpdate(selected)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: NX.textDim }}>
                  <RefreshCw size={14} />
                </button>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: NX.textDim }}>
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Summary pills */}
            <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
              {[
                { label: 'Pending', count: rollout.filter(r => r.status === 'pending').length, color: NX.textDim },
                { label: 'Notified', count: rollout.filter(r => r.status === 'notified').length, color: NX.amber },
                { label: 'Acknowledged', count: rollout.filter(r => r.status === 'acknowledged').length, color: NX.green },
              ].map(s => (
                <div key={s.label} style={{ background: NX.surface2, borderRadius: 8, padding: '0.5rem 0.875rem', textAlign: 'center' }}>
                  <div style={{ color: s.color, fontWeight: 700, fontSize: '1.25rem', lineHeight: 1 }}>{s.count}</div>
                  <div style={{ color: NX.textDim, fontSize: '0.6875rem', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Per-tenant table */}
            <div style={{ overflowY: 'auto', maxHeight: 500 }}>
              {rollout.length === 0 ? (
                <div style={{ color: NX.textDim, fontSize: '0.875rem', textAlign: 'center', padding: '2rem 0' }}>
                  {selected.published_at ? 'No tenants in queue' : 'Publish update to queue tenants'}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${NX.border}` }}>
                      {['Tenant', 'Plan', 'Status', 'Acknowledged By', 'When'].map(h => (
                        <th key={h} style={{ color: NX.textDim, fontWeight: 600, textAlign: 'left', padding: '0.5rem 0.5rem', fontSize: '0.75rem' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rollout.map(row => (
                      <tr key={row.tenant_id} style={{ borderBottom: `1px solid ${NX.border}` }}>
                        <td style={{ padding: '0.625rem 0.5rem', color: NX.text }}>{row.tenant_name}</td>
                        <td style={{ padding: '0.625rem 0.5rem', color: NX.textDim, textTransform: 'capitalize' }}>{row.plan}</td>
                        <td style={{ padding: '0.625rem 0.5rem' }}>
                          {row.status === 'acknowledged'
                            ? <span style={{ color: NX.green, display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={12} /> Acknowledged</span>
                            : row.status === 'notified'
                            ? <span style={{ color: NX.amber, display: 'flex', alignItems: 'center', gap: 4 }}><Send size={12} /> Notified</span>
                            : <span style={{ color: NX.textDim, display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={12} /> Pending</span>
                          }
                        </td>
                        <td style={{ padding: '0.625rem 0.5rem', color: NX.textDim }}>{row.acknowledged_by_name || '—'}</td>
                        <td style={{ padding: '0.625rem 0.5rem', color: NX.textDim }}>
                          {row.acknowledged_at
                            ? new Date(row.acknowledged_at).toLocaleDateString('en-AU')
                            : row.notified_at
                            ? new Date(row.notified_at).toLocaleDateString('en-AU')
                            : '—'
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Update Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.5rem', borderBottom: `1px solid ${NX.border}` }}>
              <h2 style={{ color: NX.text, fontSize: '1rem', fontWeight: 600, margin: 0 }}>New Platform Update</h2>
              <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: NX.textDim }}><X size={18} /></button>
            </div>

            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ color: NX.textDim, fontSize: '0.75rem', display: 'block', marginBottom: 4 }}>Version *</label>
                  <input value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))}
                    placeholder="2026.06.2" style={{ width: '100%', background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text, padding: '0.5rem 0.75rem', fontSize: '0.875rem', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ color: NX.textDim, fontSize: '0.75rem', display: 'block', marginBottom: 4 }}>Type</label>
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                    style={{ width: '100%', background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}>
                    <option value="feature">Feature</option>
                    <option value="fix">Bug Fix</option>
                    <option value="security">Security</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ color: NX.textDim, fontSize: '0.75rem', display: 'block', marginBottom: 4 }}>Title *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Enhanced incident analytics and mobile improvements"
                  style={{ width: '100%', background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text, padding: '0.5rem 0.75rem', fontSize: '0.875rem', boxSizing: 'border-box' }} />
              </div>

              <div>
                <label style={{ color: NX.textDim, fontSize: '0.75rem', display: 'block', marginBottom: 4 }}>Changelog *</label>
                <textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                  rows={5} placeholder={'• New: AI incident severity scoring\n• Fix: Mobile permit form submission\n• Improvement: Dashboard load time reduced by 40%'}
                  style={{ width: '100%', background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text, padding: '0.5rem 0.75rem', fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ color: NX.textDim, fontSize: '0.75rem', display: 'block', marginBottom: 4 }}>Severity</label>
                  <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
                    style={{ width: '100%', background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}>
                    <option value="normal">Normal</option>
                    <option value="important">Important</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div>
                  <label style={{ color: NX.textDim, fontSize: '0.75rem', display: 'block', marginBottom: 4 }}>Target Plans (empty = all)</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {PLAN_OPTS.map(p => (
                      <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.75rem', color: NX.textDim }}>
                        <input type="checkbox" checked={form.target_plans.includes(p)}
                          onChange={() => setForm(f => ({
                            ...f,
                            target_plans: f.target_plans.includes(p) ? f.target_plans.filter(x => x !== p) : [...f.target_plans, p]
                          }))} />
                        {p}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: NX.textDim, fontSize: '0.875rem' }}>
                <input type="checkbox" checked={form.requires_ack} onChange={e => setForm(f => ({ ...f, requires_ack: e.target.checked }))} />
                Require tenant acknowledgement (logged when dismissed)
              </label>
            </div>

            <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${NX.border}`, display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setShowCreate(false)} style={{ flex: 1, padding: '0.625rem', background: 'none', border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.textDim, cursor: 'pointer', fontSize: '0.875rem' }}>Cancel</button>
              <button onClick={handleCreate} disabled={saving} style={{ flex: 1, padding: '0.625rem', background: NX.accent, border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving…' : 'Save as Draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
